import React, { useState } from 'react';
import { Trash2, MoreVertical } from 'lucide-react';
import { getRoleColor, formatMessageTime, parseMessageContent, canDeleteMessage } from '../../utils/chatUtils';

const MessageBubble = ({ 
  message, 
  isCurrentUser, 
  onDeleteMessage, 
  currentUserRole,
  showTimestamp = true 
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!canDeleteMessage(currentUserRole) && !isCurrentUser) return;
    
    setIsDeleting(true);
    try {
      await onDeleteMessage(message.id);
    } catch (error) {
      console.error('Failed to delete message:', error);
    } finally {
      setIsDeleting(false);
      setShowActions(false);
    }
  };

  const roleColor = getRoleColor(message.sender_role);
  
  return (
    <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[280px] ${isCurrentUser ? 'order-2' : 'order-1'}`}>
        {/* Sender info for non-current user messages */}
        {!isCurrentUser && (
          <div className="flex items-center mb-1">
            <div 
              className="w-2 h-2 rounded-full mr-2" 
              style={{ backgroundColor: roleColor }}
            ></div>
            <span className="text-xs font-medium text-gray-600">
              {message.sender_display_name}
            </span>
          </div>
        )}
        
        <div className="relative group">
          <div
            className={`px-3 py-2 rounded-lg text-sm ${
              isCurrentUser
                ? 'bg-blue-500 text-white rounded-br-sm'
                : 'bg-gray-100 text-gray-800 rounded-bl-sm'
            } ${message.is_deleted ? 'opacity-50' : ''}`}
          >
            {message.is_deleted ? (
              <span className="italic text-xs">
                [DELETED] This message has been removed
              </span>
            ) : (
              <div 
                className="text-sm break-words"
                dangerouslySetInnerHTML={{ 
                  __html: parseMessageContent(message.message) 
                }}
              />
            )}
            
            {/* Action button */}
            {!message.is_deleted && (canDeleteMessage(currentUserRole) || isCurrentUser) && (
              <button
                className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-600 hover:bg-gray-700 text-white rounded-full p-1"
                onClick={() => setShowActions(!showActions)}
              >
                <MoreVertical size={12} />
              </button>
            )}
          </div>
          
          {/* Actions dropdown */}
          {showActions && (
            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center disabled:opacity-50"
              >
                <Trash2 size={14} className="mr-2" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </div>
        
        {/* Timestamp */}
        {showTimestamp && (
          <div className={`text-xs text-gray-500 mt-1 ${isCurrentUser ? 'text-right' : 'text-left'}`}>
            {formatMessageTime(message.created_at)}
            {!message.is_read && isCurrentUser && (
              <span className="ml-1 text-blue-500">●</span>
            )}
          </div>
        )}
      </div>
      
      {/* Close actions on outside click */}
      {showActions && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowActions(false)}
        />
      )}
    </div>
  );
};

export default MessageBubble;
